import Component from "@glimmer/component";
import { on } from "@ember/modifier";
import PlayIcon from "~icons/mdi/play";
import PauseIcon from "~icons/mdi/pause";
import StopIcon from "~icons/mdi/stop";
import PlusIcon from "~icons/mdi/plus";
import MinusIcon from "~icons/mdi/minus";

export interface PlayerControlsSignature {
  Args: {
    isPlaying: boolean;
    tempo: number;
    tick: number | null;
    onTogglePlay: () => void;
    onStop: () => void;
    onDecreaseTempo: () => void;
    onIncreaseTempo: () => void;
  };
  Element: HTMLDivElement;
}

export default class PlayerControlsComponent extends Component<PlayerControlsSignature> {
  get isStopped(): boolean {
    return !this.args.isPlaying && this.args.tick === null;
  }

  get positionDisplay(): string {
    const tick = this.args.tick;
    if (tick === null || tick === undefined) {
      return "- : - : -";
    }
    const sixteenth = (tick % 4) + 1;
    const beat = Math.floor((tick / 4) % 4) + 1;
    const bar = Math.floor(tick / 16) + 1;
    return `${bar} : ${beat} : ${sixteenth}`;
  }

  <template>
    <div class="flex items-center justify-between gap-6" data-test-transport-bar ...attributes>
      {{! Transport Play/Pause & Stop buttons }}
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="flex items-center gap-2 py-1 px-2 text-sm font-semibold rounded-lg transition-colors cursor-pointer
            {{if
              @isPlaying
              'bg-yellow-700 hover:bg-yellow-600 text-white'
              'bg-green-700 hover:bg-green-600 text-white'
            }}"
          title={{if @isPlaying "Pause" "Play"}}
          data-test-play-button
          {{on "click" @onTogglePlay}}
        >
          {{#if @isPlaying}}
            <PauseIcon class="w-5 h-5" />
          {{else}}
            <PlayIcon class="w-5 h-5" />
          {{/if}}
        </button>

        <button
          type="button"
          class="flex items-center gap-2 py-1 px-2 text-sm font-semibold rounded-lg bg-red-800 hover:bg-red-700 text-slate-200 transition-colors cursor-pointer
            {{if this.isStopped 'opacity-75'}}"
          title="Stop"
          data-test-stop-button
          disabled={{this.isStopped}}
          {{on "click" @onStop}}
        >
          <StopIcon class="w-5 h-5" />
        </button>
      </div>

      {{! Tempo Controls }}
      <div class="flex items-center gap-1" data-test-tempo-controls>
        <button
          type="button"
          class="w-6 h-6 flex items-center justify-center rounded bg-slate-700 hover:bg-slate-800 text-white font-bold text-sm cursor-pointer transition-colors"
          title="Decrease Tempo by 5 BPM"
          data-test-tempo-decrease
          {{on "click" @onDecreaseTempo}}
        >
          <MinusIcon class="w-4 h-4" />
        </button>

        <span
          class="font-mono text-sm font-bold text-white px-2 min-w-[64px] text-center"
          data-test-tempo-display
        >
          {{@tempo}}
          <span class="text-xs text-slate-400 font-normal">bpm</span>
        </span>

        <button
          type="button"
          class="w-6 h-6 flex items-center justify-center rounded bg-slate-700 hover:bg-slate-800 text-white font-bold text-sm cursor-pointer transition-colors"
          title="Increase Tempo by 5 BPM"
          data-test-tempo-increase
          {{on "click" @onIncreaseTempo}}
        >
          <PlusIcon class="w-4 h-4" />
        </button>
      </div>

      {{! Position Counter Display (Bar : Beat : 16th) }}
      <div
        class="flex items-center gap-2 px-4 py-1 bg-slate-900 rounded"
        data-test-position-counter
      >
        <span
          class="font-mono text-base font-bold text-white tracking-wider min-w-[70px] text-center"
          data-test-position-display
        >
          {{this.positionDisplay}}
        </span>
      </div>
    </div>
  </template>
}
